import { Spinner2 } from 'components';
import { Api } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CARD_CLASS = 'bg-white border border-[#e5e7eb] rounded-xl p-6 h-[390px] flex flex-col';

const PERIODS = ['30d', '90d', '1y', 'all'];

const ChurnCard = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChurn(period);
  }, [period]);

  async function fetchChurn(selectedPeriod) {
    setLoading(true);
    const res = await Api.get(`/admin/subscriptionPlans/overviewChurn?period=${selectedPeriod}`);
    if (res?.data) {
      setData(res.data);
    }
    setLoading(false);
  }

  const isAllTime = period === 'all';

  return (
    <div className={CARD_CLASS}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[15px] font-semibold text-[#353E4A]">
            {t('subscriptions.admin.overview.churn.title')}
          </p>
          {!isAllTime && data?.periodStart && data?.periodEnd && (
            <p className="text-[11px] text-[#97A3B5] mt-0.5">
              {new Date(data.periodStart).toLocaleDateString()} –{' '}
              {new Date(data.periodEnd).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Period filter */}
      <div className="inline-flex items-center rounded-lg border border-[#D7DEE8] bg-[#F1F5F9] p-1 mb-4 self-start shadow-[inset_0_1px_1px_rgba(15,23,42,0.06)]">
        {PERIODS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriod(p)}
            aria-pressed={period === p}
            className={`px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-all duration-150 ${
              period === p
                ? 'bg-[#334155] text-white shadow-[0_2px_6px_rgba(15,23,42,0.28)]'
                : 'text-[#64748B] hover:text-[#334155] hover:bg-white/70'
            }`}
          >
            {t(`subscriptions.admin.overview.churn.periods.${p}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Spinner2 />
        </div>
      ) : isAllTime ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <p className="text-[36px] font-bold text-[#353E4A]">
            {t('subscriptions.admin.overview.churn.na')}
          </p>
          <p className="text-[12px] text-[#97A3B5] mt-2">
            {t('subscriptions.admin.overview.churn.allTimeNote')}
          </p>
        </div>
      ) : data?.cancelledCount === 0 && data?.activeDuringPeriod === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 rounded-xl bg-[#F0F2F4] flex items-center justify-center mb-4">
            <svg
              className="w-5 h-5 text-[#B5BDCA]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <p className="text-[13px] text-[#97A3B5]">
            {t('subscriptions.admin.overview.churn.empty')}
          </p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Churn rate hero */}
          <div className="mb-5">
            <p className="text-[40px] font-bold text-[#353E4A] leading-none">
              {data?.churnRate != null
                ? `${data.churnRate}%`
                : t('subscriptions.admin.overview.churn.na')}
            </p>
            <p className="text-[12px] text-[#97A3B5] mt-1.5">
              {t('subscriptions.admin.overview.churn.subtitle')}
            </p>
          </div>

          {/* Simple stats */}
          <div className="space-y-2">
            <div className="flex items-center justify-between py-1.5">
              <span className="text-[13px] text-[#7989A0]">
                {t('subscriptions.admin.overview.churn.cancelled')}
              </span>
              <span className="text-[13px] font-medium text-[#353E4A]">
                {data?.cancelledCount ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-1.5">
              <span className="text-[13px] text-[#7989A0]">
                {t('subscriptions.admin.overview.churn.activeDuringPeriod')}
              </span>
              <span className="text-[13px] font-medium text-[#353E4A]">
                {data?.activeDuringPeriod ?? 0}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChurnCard;
